import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import SentryBoundary from './components/SentryBoundary';
import './index.css';
import App from './App';
import { isBuilderEnabled } from './builder/config';

const DSN = import.meta.env.VITE_SENTRY_DSN;
if (DSN) {
  Sentry.init({
    dsn: DSN,
    // Free-tier friendly: no tracing/replay
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Tag builds for grouping & triage
    release: (import.meta as any).env?.VERCEL_GIT_COMMIT_SHA || undefined,
    environment:
      (import.meta as any).env?.SENTRY_ENV ||
      (import.meta as any).env?.VERCEL_ENV ||
      import.meta.env.MODE ||
      'production',
    // Protect user data; keep payloads lean
    sendDefaultPii: false,
    beforeSend(event) {
      // Strip query strings and fragments
      if (event.request?.url) {
        try {
          const u = new URL(event.request.url);
          u.search = '';
          u.hash = '';
          event.request.url = u.toString();
        } catch {}
      }
      // Remove common noisy errors we don't control (example: favicon preload)
      const msg = event.exception?.values?.[0]?.value || '';
      if (/favicon.*preload/i.test(msg)) return null;
      return event;
    },
    // Reduce noise from devtools extensions
    denyUrls: [/extensions\//i, /^chrome:\/\//i],
  });

  // Helpers for /ops
  (window as any).__sentryEnabled = true;
  (window as any).__sentryTest = () => {
    Sentry.captureException(new Error('[ops] manual sentry test'));
  };
}

// Conditionally register Builder components (tree-shakeable when disabled)
if (isBuilderEnabled() && typeof window !== 'undefined') {
  import('./builder/register').then((mod) => mod.registerBuilderComponents()).catch(() => {
    // Silently fail if Builder is not available
  });
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  document.body.innerHTML = "<pre style='padding:24px'>Root element #root not found.</pre>";
} else {
  const root = ReactDOM.createRoot(rootEl);
  root.render(
    <React.StrictMode>
      <SentryBoundary>
        <App />
      </SentryBoundary>
    </React.StrictMode>,
  );
}
